import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Button, Paper, Typography, Table, TableContainer, TableHead, TableRow,  
  TableCell, TableBody, Select, MenuItem, FormControl, InputLabel, IconButton,
  Alert
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { formatEuro } from '../../utils/currency';
import { InvoiceDto, InvoiceProductDto, ProductDto, getProducts } from '../../services/expensesApi';

interface InvoiceFormProps {
  initial?: Partial<InvoiceDto>;
  onSubmit: (data: Partial<InvoiceDto>) => void;
  onCancel?: () => void;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ initial = {}, onSubmit, onCancel }) => {
  // Helper function to format date for HTML date input
  const formatDateForInput = (dateString?: string): string => {
    if (!dateString) return '';
    // Handle both full datetime strings and date-only strings
    return dateString.split('T')[0];
  };

  // Helper function to normalize product data for IVA compatibility
  const normalizeProducts = (products: InvoiceProductDto[] = []): InvoiceProductDto[] => {
    return products.map(product => ({
      ...product,
      // Ensure IVA fields are present, use defaults only if undefined
      priceWithoutIva: product.priceWithoutIva ?? 0,
      ivaRate: product.ivaRate !== undefined ? product.ivaRate : 23 // Default to 23% IVA only if field is missing
    }));
  };

  // Format the initial date for HTML date input
  const initialDate = formatDateForInput(initial.date);
  
  const [date, setDate] = useState(initialDate);
  const [notes, setNotes] = useState(initial.notes || '');
  const [products, setProducts] = useState<InvoiceProductDto[]>(normalizeProducts(initial.products));
  
  // Available products for selection
  const [availableProducts, setAvailableProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // New product form state
  const [selectedProductId, setSelectedProductId] = useState('');
  const [quantity, setQuantity] = useState<number>(1);
  const [price, setPrice] = useState<number>(0);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const productList = await getProducts();
        setAvailableProducts(productList);
      } catch (err) {
        setError('Não foi possível carregar os produtos');
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Update form when initial data changes (for editing different invoices)
  useEffect(() => {
    setDate(formatDateForInput(initial.date));
    setNotes(initial.notes || '');
    setProducts(normalizeProducts(initial.products));
  }, [initial]); // Empty dependency array - only load once

  // Update form when initial values change (for editing)
  useEffect(() => {
    if (initial) {
      // Format the date for HTML date input (YYYY-MM-DD)
      const formattedDate = formatDateForInput(initial.date);
      setDate(formattedDate);
      setNotes(initial.notes || '');
      setProducts(initial.products || []);
    }
  }, [initial]);

  const addProductToInvoice = () => {
    if (!selectedProductId || quantity <= 0 || price <= 0) {
      setError('Selecione um produto e indique uma quantidade e um preço válidos');
      return;
    }

    const selectedProduct = availableProducts.find(p => p.publicId === selectedProductId);
    if (!selectedProduct) {
      setError('Produto selecionado não encontrado');
      return;
    }

    // Check if product already exists in the invoice
    const existingIndex = products.findIndex(p => p.productPublicId === selectedProductId);
    if (existingIndex >= 0) {
      // Update existing product
      const updatedProducts = [...products];
      updatedProducts[existingIndex] = {
        ...updatedProducts[existingIndex],
        quantity: updatedProducts[existingIndex].quantity + quantity,
        priceWithoutIva: price, // Use the new price
        ivaRate: updatedProducts[existingIndex].ivaRate // Keep existing IVA rate
      };
      setProducts(updatedProducts);
    } else {
      // Add new product
      const newProduct: InvoiceProductDto = {
        productPublicId: selectedProductId,
        productName: selectedProduct.name,
        quantity: quantity,
        priceWithoutIva: price,
        ivaRate: 23 // Default IVA rate for Portugal/Spain
      };
      setProducts([...products, newProduct]);
    }

    // Reset form
    setSelectedProductId('');
    setQuantity(1);
    setPrice(0);
    setError('');
  };

  const removeProduct = (index: number) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const updateProductQuantity = (index: number, newQuantity: number) => {
    if (newQuantity <= 0) return;
    const updatedProducts = [...products];
    updatedProducts[index].quantity = newQuantity;
    setProducts(updatedProducts);
  };

  const updateProductPrice = (index: number, newPrice: number) => {
    if (newPrice < 0) return;
    const updatedProducts = [...products];
    updatedProducts[index].priceWithoutIva = newPrice;
    setProducts(updatedProducts);
  };

  const updateProductIvaRate = (index: number, newIvaRate: number) => {
    if (newIvaRate < 0 || newIvaRate > 100) return;
    const updatedProducts = [...products];
    updatedProducts[index].ivaRate = newIvaRate;
    setProducts(updatedProducts);
  };

  const calculateProductTotal = (product: InvoiceProductDto, withIva: boolean = true) => {
    const subtotal = product.quantity * product.priceWithoutIva;
    if (withIva) {
      return subtotal * (1 + product.ivaRate / 100);
    }
    return subtotal;
  };

  const calculateTotal = (withIva: boolean = true) => {
    return products.reduce((total, product) => total + calculateProductTotal(product, withIva), 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ 
      date, 
      notes, 
      products: products.length > 0 ? products : undefined 
    });
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {initial.publicId ? 'Editar fatura' : 'Adicionar fatura'}
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          label="Data"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          fullWidth
          margin="normal"
          InputLabelProps={{ shrink: true }}
          required
        />
        
        <TextField
          label="Notas"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          fullWidth
          margin="normal"
          multiline
          rows={2}
        />

        {/* Add Products Section */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Produtos
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2, 
          mb: 2, 
          alignItems: { xs: 'stretch', sm: 'end' }
        }}>
          <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
            <InputLabel>Produto</InputLabel>
            <Select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              label="Produto"
              disabled={loading}
            >
              {availableProducts.map(product => (
                <MenuItem key={product.publicId} value={product.publicId}>
                  {product.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Quantidade"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            inputProps={{ min: 1 }}
            sx={{ width: { xs: '100%', sm: 100 } }}
          />
          
          <TextField
            label="Preço"
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            inputProps={{ min: 0, step: 0.0001 }}
            sx={{ width: { xs: '100%', sm: 120 } }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <IconButton 
              onClick={addProductToInvoice} 
              color="primary"
              disabled={loading}
              size="large"
            >
              <Add />
            </IconButton>
          </Box>
        </Box>

        {/* Products Table */}
        {products.length > 0 && (
          <TableContainer sx={{ mb: 2 }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Qtd.</TableCell>
                  <TableCell align="right">Preço (sem IVA)</TableCell>
                  <TableCell align="right">IVA %</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ minWidth: 120 }}>{product.productName}</TableCell>
                    <TableCell align="right" sx={{ minWidth: 80 }}>
                      <TextField
                        type="number"
                        value={product.quantity}
                        onChange={(e) => updateProductQuantity(index, Number(e.target.value))}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: 70 }}
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      <TextField
                        type="number"
                        value={product.priceWithoutIva}
                        onChange={(e) => updateProductPrice(index, Number(e.target.value))}
                        inputProps={{ min: 0, step: 0.0001 }}
                        size="small"
                        sx={{ width: 90 }}
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 80 }}>
                      <TextField
                        type="number"
                        value={product.ivaRate}
                        onChange={(e) => updateProductIvaRate(index, Number(e.target.value))}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        size="small"
                        sx={{ width: 70 }}
                        variant="standard"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 120 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatEuro(calculateProductTotal(product, false))}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatEuro(calculateProductTotal(product, true))}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 60 }}>
                      <IconButton 
                        onClick={() => removeProduct(index)} 
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total sem IVA: {formatEuro(calculateTotal(false))}
                      </Typography>
                      <Typography variant="h6">
                        Total com IVA: {formatEuro(calculateTotal(true))}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: { sm: 'flex-start' },
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            size="large"
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Guardar fatura
          </Button>
          {onCancel && (
            <Button 
              variant="outlined" 
              onClick={onCancel}
              size="large"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Cancelar
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default InvoiceForm;
